import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Loader2, FlaskConical } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { errMsg } from "./mk";

const TestRow = ({ t }) => (
  <li className={t.ok ? "text-emerald-800" : "text-red-700"}>
    {new Date(t.at).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })} · {t.platform}{t.platform === "instagram" ? " (prepared, not published)" : " (hidden)"} · {t.post_title || "post"} · {t.ok ? "OK" : t.error}
  </li>
);

export default function MetaLiveTest({ s, onUpdate }) {
  const [posts, setPosts] = useState([]);
  const [pid, setPid] = useState("");
  const [busy, setBusy] = useState("");
  useEffect(() => {
    api.get("/admin/marketing/posts").then(({ data }) => setPosts(data.filter((p) => ["approved", "scheduled"].includes(p.status) && p.image_path))).catch(() => {});
  }, [s.tests?.length]);
  const healthy = s.last_check?.ok;
  const test = async (platform) => {
    const msg = platform === "facebook"
      ? "Send a HIDDEN test to Facebook? It is uploaded to your Page but not shown to followers."
      : "Check Instagram access? Instagram prepares the post to confirm permissions and the image, but it is NOT published.";
    if (!window.confirm(msg)) return;
    setBusy(platform);
    try {
      const { data } = await api.post("/admin/marketing/meta/test-post", { post_id: pid, platform, confirm: true });
      data.test.ok ? toast.success(`${platform} test worked`) : toast.error(data.test.error, { duration: 9000 });
      onUpdate(data.status);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(""); }
  };
  const toggleLive = async (on) => {
    if (on && !window.confirm("Turn ON live posting? From now on, approved and scheduled posts will really be published.")) return;
    try { const { data } = await api.post("/admin/marketing/meta/live", { enabled: on, confirm: on }); onUpdate(data); toast.success(on ? "Live posting ON" : "Live posting OFF"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-3 border-t border-slate-100 pt-3" data-testid="meta-live-test">
      <h4 className="flex items-center gap-1 text-sm font-bold text-slate-800"><FlaskConical className="h-4 w-4" /> Live test (one post, only when you click)</h4>
      <Select value={pid} onValueChange={setPid}>
        <SelectTrigger data-testid="meta-test-post-select"><SelectValue placeholder={posts.length ? "Choose an approved post with an image" : "Approve a post with an image first"} /></SelectTrigger>
        <SelectContent>{posts.map((p) => <SelectItem key={p.id} value={p.id}>{p.title || p.caption.slice(0, 40)}</SelectItem>)}</SelectContent>
      </Select>
      <div className="flex flex-wrap gap-2">
        {["facebook", "instagram"].map((pf) => (
          <Button key={pf} size="sm" variant="outline" disabled={!pid || !healthy || !!busy} onClick={() => test(pf)} data-testid={`meta-test-${pf}`}>
            {busy === pf && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}Test {pf === "facebook" ? "Facebook (hidden)" : "Instagram (not published)"}
          </Button>
        ))}
      </div>
      {!!s.tests?.length && <ul className="space-y-0.5 text-[11px]" data-testid="meta-test-list">{s.tests.slice().reverse().map((t) => <TestRow key={t.at + t.platform} t={t} />)}</ul>}
      <div className="flex items-center gap-3 rounded-xl bg-slate-50 p-3">
        <Switch checked={s.live_enabled} onCheckedChange={toggleLive} data-testid="meta-live-switch" />
        <div className="text-xs">
          <div className="font-bold text-slate-800">Live posting {s.live_enabled ? "ON" : "OFF"}</div>
          <div className="text-slate-500">Can be turned on only after a healthy check and one successful test on each platform.</div>
        </div>
      </div>
    </div>
  );
}
